import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

interface ValidationData {
  ai_answer?: string;
  tape?: string;
  source?: string;
  answer?: string;
  validation?: string;
  value?: string;
  is_source_and_tape_value_same?: boolean | null;
  has_done_validation?: boolean;
}

@Component({
  selector: 'app-validation-source-modal',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatRadioModule, MatIcon, MatIconModule, FormsModule, MatTooltipModule],
  templateUrl: './validation-source-modal.component.html',
  styleUrls: ['./validation-source-modal.component.scss']
})

export class ValidationSourceModalComponent {
  selectedSource: 'TAPE' | 'SOURCE' | null = null;
  userNote: string = '';
  // Tracks whether the user has typed into the Manual Input field.
  userTypedManual: boolean = false;
  constructor(
    public dialogRef: MatDialogRef<ValidationSourceModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ValidationData
  ) {}
  // Simple tooltip text shown when radio buttons are disabled
  tooltipText = 'Radio choices disabled. You can still add a Manual Input.';

  // Computed property: disable radio options when tape and source values are both present and identical
  get radiosDisabled(): boolean {
    const tape = (this.data && (this.data.tape || this.data.ai_answer) || '').toString().trim();
    const source = (this.data && (this.data.source || '') || '').toString().trim();
    // If both tape and source are present and system validation hasn't been performed
    // but the values are known to be different, ensure radios are enabled so user
    // can choose between Tape/Source. This overrides other disabling rules below.
    const hasDoneValidation = this.data && typeof this.data.has_done_validation !== 'undefined' ? this.data.has_done_validation : undefined;
    const isSourceTapeSameFlag = this.data && typeof this.data.is_source_and_tape_value_same !== 'undefined' ? this.data.is_source_and_tape_value_same : undefined;
    if (tape !== '' && source !== '' && hasDoneValidation === false && isSourceTapeSameFlag === false) {
      return false;
    }
    // Also enable radios when AI candidate list contains both Tape and Source entries
    // and the system flags indicate validation not done and values differ.
    const aiList = this.data && (this.data as any).ai_answer_list;
    if (Array.isArray(aiList) && aiList.length > 0 && hasDoneValidation === false && isSourceTapeSameFlag === false) {
      const hasTapeEntry = aiList.some((i: any) => (i.category || '').toString().toLowerCase().includes('tape'));
      const hasSourceEntry = aiList.some((i: any) => (i.category || '').toString().toLowerCase().includes('source'));
      if (hasTapeEntry && hasSourceEntry) {
        return false;
      }
    }
    // Keep radios disabled when tape and source are identical
    if (tape !== '' && source !== '' && tape === source) return true;

    // Disable radios when the system hasn't validated and the "is_source_and_tape_value_same" flag is null
    // This covers cases where automated validation hasn't been performed yet.
    const isSameFlag = this.data && typeof this.data.is_source_and_tape_value_same !== 'undefined' ? this.data.is_source_and_tape_value_same : undefined;
    if (isSameFlag === null && hasDoneValidation === false) return true;

    // Also disable radios for any explicit MANUAL validation
    const validation = (this.data && (this.data.validation || '') || '').toString().trim().toUpperCase();
    if (validation === 'MANUAL') return true;

    // If AI provided candidate list and it's non-empty, enable tape/source radios
    const hasAiList = Array.isArray(aiList) && aiList.length > 0;
    const hasManualInput = this.userNote && this.userNote.toString().trim().length > 0;
    // If there are AI candidates and no manual override, enable radios (unless validation is MANUAL handled above)
    if (hasAiList) {
      // If the AI list contains only manual-category entries, treat it as empty (do not enable radios)
      const onlyManual = aiList.every((i: any) => ((i.category || '').toString().toLowerCase().includes('manual')));
      if (onlyManual) return true;
      // If any ai result explicitly indicates 'Not found in documents', do NOT disable radios
      // (allow the user to still choose Tape/Source). Previously this forced manual-only.
      const anyNotFound = aiList.some((i: any) => ((i.result || '').toString().toLowerCase().trim() === 'not found in documents'));
      if (anyNotFound) return false;
      if (hasManualInput) return true;
      return false;
    }

    // New rule: if AI provided an empty list AND ai_category null AND has_done_validation null AND is_source_and_tape_value_same null
    // then only allow manual input (disable tape/source radios)
    const isAiListEmptyArray = Array.isArray(aiList) && aiList.length === 0;
    const aiCategory = this.data && (this.data as any).ai_category;
    const hasDoneValidationField = this.data && typeof (this.data as any).has_done_validation !== 'undefined' ? (this.data as any).has_done_validation : null;
    const isSourceTapeSame = this.data && typeof (this.data as any).is_source_and_tape_value_same !== 'undefined' ? (this.data as any).is_source_and_tape_value_same : null;
    if (isAiListEmptyArray && aiCategory == null && hasDoneValidationField == null && isSourceTapeSame == null) {
      return true;
    }

    // If user is typing a manual note, disable the radio buttons to avoid mixed input
    if (this.userNote && this.userNote.toString().trim().length > 0) return true;

    return false;
  }

  // Whether the confirm button should be enabled
  get canSubmit(): boolean {
    const hasNote = this.userNote.toString().trim().length > 0;
    // If radios are disabled, only allow submit when user added manual note
    if (this.radiosDisabled) return hasNote;
    return Boolean(!!this.selectedSource || hasNote);
  }

  // Handler for manual input changes. If the user types any non-empty text,
  // clear the radio selection to enforce manual-only input and keep radios disabled.
  onUserNoteChange(value: string) {
    this.userNote = value || '';
    const hasText = this.userNote.toString().trim().length > 0;
    this.userTypedManual = hasText;
    if (hasText) {
      this.selectedSource = null;
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }

  confirm() {
    // Allow submission when radios are disabled, a radio is selected, or manual note exists.
    if (!this.canSubmit) return;
    const hasNote = this.userNote.toString().trim().length > 0;
    let selected: 'TAPE' | 'SOURCE' | null = this.selectedSource;

    // If radios are disabled and user didn't select anything and didn't add a note,
    // default to SOURCE per requirement.
    if (this.radiosDisabled && !selected && !hasNote) {
      selected = 'SOURCE';
    }

    // Do not send both a radio selection and manual note together.
    const payload: any = { selected: hasNote ? null : selected };
    if (hasNote) {
      payload.note = this.userNote.trim();
    }
    this.dialogRef.close(payload);
  }
}

